import { twMerge } from 'tailwind-merge';

interface UserAvatarProps {
  name: string | undefined;
  picture: string | undefined;
  size?: number;
  class_name?: string;
}

/**
 * Returns up to two initials from a full name.
 * "Carlos Ramírez" → "CR"
 * "Carlos" → "C"
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Derives a deterministic background color from a string so the same
 * user always gets the same color across sessions.
 * Returns one of 8 hand-picked colors that look good on both themes.
 */
function getBackgroundColor(name: string): string {
  const colors = [
    '#4f7fe0', // blue
    '#7c5cbf', // purple
    '#d97706', // amber
    '#059669', // emerald
    '#dc2626', // red
    '#0891b2', // cyan
    '#65a30d', // lime
    '#db2777', // pink
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

/**
 * Shows the user's profile picture when available.
 * Falls back to a circular lettered avatar with a deterministic background
 * color derived from the user's name — no external API needed.
 */
export function UserAvatar(props: UserAvatarProps) {
  const size = props.size ?? 32;
  const name = props.name ?? '?';

  if (props.picture) {
    return (
      <img
        src={props.picture}
        alt={name}
        width={size}
        height={size}
        className={twMerge('rounded-full object-cover shrink-0', props.class_name)}
        style={{ width: size, height: size }}
        referrerPolicy="no-referrer"
      />
    );
  }

  const initials = name === '?' ? '?' : getInitials(name);
  const background = name === '?' ? '#6b7280' : getBackgroundColor(name);
  const font_size = size <= 28 ? 10 : size <= 36 ? 12 : 14;

  return (
    <div
      className={twMerge('flex items-center justify-center rounded-full shrink-0 select-none font-semibold text-white', props.class_name)}
      style={{ width: size, height: size, backgroundColor: background, fontSize: font_size }}
      title={name}
    >
      {initials}
    </div>
  );
}
